import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Alert, Loader } from '@itds/elements/index.js';
import Config from '@/config';
import AnnuityQuoterShow from '@brokerage/pages/agencies/annuity_quoters/Show';
import LifeQuoterShow from '@brokerage/pages/agencies/agency_life_quoters/Show';
import LTCQuoterShow from '@brokerage/pages/agencies/ltc_quoters/Show';
import General from './General';
import H1 from '@brokerage/components/H1';
import { Api } from '@brokerage/utils/Api';
import ThemeOptions from './ThemeOptions';
import EmbedCode from './EmbedCode';

export const Show = () => {
  const params = useParams();

  const [universal_quoter, setUniversalQuoter] = useState(null);
  const [universal_quoter_details, setUniversalQuoterDetails] = useState([]);
  const [error_message, setErrorMessage] = useState(null);
  const [success_message, setSuccessMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedProductTab, setSelectedProductTab] = useState('universal_general');
  const [selectedVerticalTab, setSelectedVerticalTab] = useState('general');
  const [annuityQuoterData, setAnnuityQuoterData] = useState(null);
  const [lifeQuoterData, setLifeQuoterData] = useState(null);
  const [ltcQuoterData, setLtcQuoterData] = useState(null);

  useEffect(() => {
    setLoading(true);
    Api.loadUniversalQuoter(params.id)
      .then(({ data }) => {
        setUniversalQuoter(data || {});
        const details = (data && data.universal_quoter_details) || [];

        setUniversalQuoterDetails(details);
        setSelectedProductTab('universal_general');
        setSelectedVerticalTab('quoter_overview');

        // If this is a new universal quoter (no details), reset tabs to overview
        if (!data || !details || details.length === 0) {
          setSelectedProductTab('universal_general');
          setSelectedVerticalTab('quoter_overview');
          setLoading(false);
          return;
        }

        // Preload sub-quoter data for immediate availability when tabs are accessed
        const loadPromises = [];

        // Load Annuity (index 0)
        if (details[0] && details[0].quoter_token_id) {
          loadPromises.push(
            Api.loadAnnuityQuoter(details[0].quoter_token_id)
              .then(({ data }) => {
                setAnnuityQuoterData(data);
              })
              .catch(() => {
                // Silently handle errors for preloading
              })
          );
        }

        // Load Life (index 1)
        if (details[1] && details[1].quoter_token_id) {
          loadPromises.push(
            Api.loadBgaQuoter(details[1].quoter_token_id)
              .then(({ data }) => {
                setLifeQuoterData(data);
              })
              .catch(() => {
                // Silently handle errors for preloading
              })
          );
        }

        // Load LTC (index 2)
        if (details[2] && details[2].quoter_token_id) {
          loadPromises.push(
            Api.loadLtcQuoter(details[2].quoter_token_id)
              .then(({ data }) => {
                setLtcQuoterData(data);
              })
              .catch(() => {
                // Silently handle errors for preloading
              })
          );
        }

        // Wait for all preload operations to complete, then stop loading
        Promise.allSettled(loadPromises).finally(() => {
          setLoading(false);
        });
      })
      .catch(() => {
        setLoading(false);
      });
  }, [params.id]);

  // Load sub-quoter data on demand if not already cached (fallback for tab switching)
  useEffect(() => {
    if (!universal_quoter_details || universal_quoter_details.length === 0) return;

    let detailIdx = 0;
    let token = null;

    if (selectedProductTab === 'life') detailIdx = 1;
    else if (selectedProductTab === 'ltc') detailIdx = 2;

    const detail = universal_quoter_details[detailIdx];

    token = detail && detail.quoter_token_id;

    // Only load if we don't already have cached data
    if (selectedProductTab === 'annuity' && token && !annuityQuoterData) {
      Api.loadAnnuityQuoter(token).then(({ data }) => setAnnuityQuoterData(data));
    } else if (selectedProductTab === 'life' && token && !lifeQuoterData) {
      Api.loadBgaQuoter(token).then(({ data }) => setLifeQuoterData(data));
    } else if (selectedProductTab === 'ltc' && token && !ltcQuoterData) {
      Api.loadLtcQuoter(token).then(({ data }) => setLtcQuoterData(data));
    }
    // Check if the new tab's quoter is deactivated and set error message accordingly
    onHandleErrorPopModal();
    }, [
    selectedProductTab,
    universal_quoter_details,
    universal_quoter,
    annuityQuoterData,
    lifeQuoterData,
    ltcQuoterData
  ]);

  const onHandleErrorPopModal = () => {
    let hasSetMessage = null;
    const isUQ = universal_quoter?.is_deactivated;
    const isAAQ = annuityQuoterData?.is_deactivated;
    const isALQ = lifeQuoterData?.is_deactivated;

    if (isUQ) {
      hasSetMessage = 'Universal Quoter is Inactive';
    } else if (isAAQ && isALQ) {
      hasSetMessage = 'Annuity Quoter and Life Quoter are Inactive';
    } else if (isAAQ) {
      hasSetMessage = 'Annuity Quoter is Inactive';
    } else if (isALQ) {
      hasSetMessage = 'Life Quoter is Inactive';
    }
    setErrorMessage(hasSetMessage);
  }

  const handleSave = (input_fields, detailIndex = null) => {
    window.scrollTo(0, 0);
    setLoading(true);
    let payload;
    let savePromise;

    // Universal General tab: updateUniversalQuoter with token and payload
    if (selectedProductTab === 'universal_general') {
      const token =
        universal_quoter &&
        (universal_quoter.token || universal_quoter.quoter_token_id || params.id);

      payload = { ...universal_quoter, ...input_fields };
      savePromise = Api.updateUniversalQuoter(token, payload)
        .then(() => Api.loadUniversalQuoter(token))
        .then(({ data }) => {
          setUniversalQuoter(data || {});
          setUniversalQuoterDetails((data && data.universal_quoter_details) || []);
        });
    } else if (detailIndex !== null && universal_quoter_details[detailIndex]) {
      const detail = universal_quoter_details[detailIndex];
      const token = detail && detail.quoter_token_id;

      if (selectedProductTab === 'annuity') {
        setAnnuityQuoterData(null); // Clear cache to force reload
        savePromise = Api.updateAnnuityQuoter(token, input_fields).then(() =>
          Api.loadAnnuityQuoter(token).then(({ data }) => {
            setAnnuityQuoterData(data);
          })
        );
      } else if (selectedProductTab === 'life') {
        setLifeQuoterData(null); // Clear cache to force reload
        savePromise = Api.updateBgaQuoter(token, input_fields).then(() =>
          Api.loadBgaQuoter(token).then(({ data }) => {
            setLifeQuoterData(data);
          })
        );
      } else if (selectedProductTab === 'ltc') {
        setLtcQuoterData(null); // Clear cache to force reload
        savePromise = Api.updateLtcQuoter(token, input_fields).then(() =>
          Api.loadLtcQuoter(token).then(({ data }) => {
            setLtcQuoterData(data);
          })
        );
      } else {
        // fallback to universal quoter update
        const updatedDetails = [...universal_quoter_details];

        updatedDetails[detailIndex] = {
          ...universal_quoter_details[detailIndex],
          ...input_fields
        };
        payload = { ...(universal_quoter || {}), universal_quoter_details: updatedDetails };
        savePromise = Api.updateUniversalQuoter(params.id, payload).then(({ data }) => {
          setUniversalQuoter(data || {});
          setUniversalQuoterDetails((data && data.universal_quoter_details) || []);
        });
      }
    } else {
      payload = { ...(universal_quoter || {}), ...input_fields };
      savePromise = Api.updateUniversalQuoter(params.id, payload).then(({ data }) => {
        setUniversalQuoter(data || {});
        setUniversalQuoterDetails((data && data.universal_quoter_details) || []);
      });
    }

    savePromise
      .then(() => {
        setErrorMessage(null);
        setSuccessMessage('Quoter updated successfully');
        setTimeout(() => {
          setSuccessMessage(null);
        }, 2000);
      })
      .catch(() => {
        setSuccessMessage(null);
        setErrorMessage('Unable to update Quoter');
        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      })
      .finally(() => setLoading(false));
  };

  const TOP_TABS = [
    { key: 'universal_general', label: 'Universal General' },
    { key: 'annuity', label: 'Annuity' },
    { key: 'life', label: 'Life' },
    { key: 'ltc', label: 'LTC' }
  ];

  const getVerticalTabs = () => {
    if (selectedProductTab === 'universal_general') {
      return [
        { key: 'quoter_overview', label: 'Quoter Overview' },
        { key: 'themes', label: 'Themes' },
        { key: 'preview', label: 'Preview' },
        { key: 'embed', label: 'Embed' }
      ];
    } else {
      // Default tabs for all products
      let tabs = [
        { key: 'product_defaults', label: 'Product Defaults' },
        { key: 'limit_states', label: 'Limit States' },
        { key: 'limit_products', label: 'Limit Products' },
        { key: 'limit_carriers', label: 'Limit Carriers' },
        { key: 'custom_actions', label: 'Custom Actions' }
      ];
      // Remove Theme tab for LTC (defensive, in case it is ever added)

      if (selectedProductTab === 'ltc') {
        tabs = tabs.filter(tab => tab.key !== 'themes');
      }
      return tabs;
    }
  };

  const isProductSelected =
    selectedProductTab === 'annuity' ||
    selectedProductTab === 'life' ||
    selectedProductTab === 'ltc';

  function renderVerticalTabContent() {
    if (selectedProductTab === 'universal_general') {
      if (selectedVerticalTab === 'quoter_overview') {
        return (
          <General
            handleSave={fields => handleSave(fields, null)}
            universal_quoter={universal_quoter}
          />
        );
      }
      if (selectedVerticalTab === 'themes') {
        return (
          <ThemeOptions
            onReload={() => {
              Api.loadUniversalQuoter(params.id).then(({ data }) => {
                setUniversalQuoter(data || {});
                setUniversalQuoterDetails((data && data.universal_quoter_details) || []);
              });
            }}
            setErrorMessage={setErrorMessage}
            setSuccessMessage={setSuccessMessage}
            universal_quoter={universal_quoter}
          />
        );
      }
      if (selectedVerticalTab === 'preview') {
        if (universal_quoter && universal_quoter.token) {
          window.open(`${Config.UQ_URL}?token=${universal_quoter.token}`);
        }
        setSelectedVerticalTab('quoter_overview');
        return null;
      }
      if (selectedVerticalTab === 'embed') {
        return <EmbedCode universal_quoter={universal_quoter} />;
      }
      return null;
    }
    if (isProductSelected) {
      let detailIdx = 0;

      if (selectedProductTab === 'life') detailIdx = 1;
      else if (selectedProductTab === 'ltc') detailIdx = 2;
      const detail = universal_quoter_details && universal_quoter_details[detailIdx];
      const token = detail && detail.quoter_token_id;
      // Use cached data if available
      const annuityDetail = annuityQuoterData || detail;
      const lifeDetail = lifeQuoterData || detail;
      const ltcDetail = ltcQuoterData || detail;
      // Only render the sub-quoter's General for Product Defaults tab

      if (selectedVerticalTab === 'product_defaults') {
        if (selectedProductTab === 'annuity') {
          return (
            <AnnuityQuoterShow.General
              isUQDeactivated={universal_quoter?.is_deactivated}
              annuity_quoter={annuityDetail}
              handleSave={fields => handleSave(fields, detailIdx)}
              token={token}
            />
          );
        } else if (selectedProductTab === 'life') {
          return (
            <LifeQuoterShow.General
              isUQDeactivated={universal_quoter?.is_deactivated}
              bga_quoter={lifeDetail}
              controlledMode={true}
              handleSave={fields => handleSave(fields, detailIdx)}
              token={token}
            />
          );
        } else if (selectedProductTab === 'ltc') {
          return (
            <LTCQuoterShow.General
              controlledMode={true}
              handleSave={fields => handleSave(fields, detailIdx)}
              ltc_quoter={ltcDetail}
              token={token}
            />
          );
        }
      }
      // For other tabs, render the sub-quoter Show.js in controlled mode
      if (selectedProductTab === 'annuity') {
        return (
          <AnnuityQuoterShow
            controlledTabKey={selectedVerticalTab}
            controlledTabs={getVerticalTabs()}
            detail={annuityDetail}
            onParentSave={fields => handleSave(fields, detailIdx)}
            onTabChange={setSelectedVerticalTab}
            token={token}
          />
        );
      } else if (selectedProductTab === 'life') {
        return (
          <LifeQuoterShow
            controlledTabKey={selectedVerticalTab}
            controlledTabs={getVerticalTabs()}
            detail={lifeDetail}
            onParentSave={(fields, { setErrorMessage, setSuccessMessage }) => {
              setLoading(true);
              setLifeQuoterData(null); // Clear cache to force reload
              Api.updateBgaQuoter(token, fields)
                .then(() =>
                  Api.loadBgaQuoter(token).then(({ data }) => {
                    setLifeQuoterData(data);
                    setErrorMessage(null);
                    setSuccessMessage('ALQ updated successfully');
                    setTimeout(() => {
                      setSuccessMessage(null);
                    }, 2000);
                  })
                )
                .catch(() => {
                  setSuccessMessage(null);
                  setErrorMessage('Unable to update ALQ');
                  setTimeout(() => {
                    setErrorMessage(null);
                  }, 2000);
                })
                .finally(() => setLoading(false));
            }}
            onTabChange={setSelectedVerticalTab}
            token={token}
          />
        );
      } else if (selectedProductTab === 'ltc') {
        return (
          <LTCQuoterShow
            controlledTabKey={selectedVerticalTab}
            controlledTabs={getVerticalTabs()}
            detail={ltcDetail}
            onParentSave={(fields, { setErrorMessage, setSuccessMessage }) => {
              setLoading(true);
              setLtcQuoterData(null); // Clear cache to force reload
              Api.updateLtcQuoter(token, fields)
                .then(() =>
                  Api.loadLtcQuoter(token).then(({ data }) => {
                    setLtcQuoterData(data);
                    setErrorMessage(null);
                    setSuccessMessage('LTC updated successfully');
                    setTimeout(() => {
                      setSuccessMessage(null);
                    }, 2000);
                  })
                )
                .catch(() => {
                  setSuccessMessage(null);
                  setErrorMessage('Unable to update LTC');
                  setTimeout(() => {
                    setErrorMessage(null);
                  }, 2000);
                })
                .finally(() => setLoading(false));
            }}
            onTabChange={setSelectedVerticalTab}
            token={token}
          />
        );
      }
    }
    return null;
  }

  return loading ? (
    <Loader />
  ) : (
    <>
      {error_message ? <Alert type='error'>{error_message}</Alert> : null}
      {success_message ? <Alert type='success'>{success_message}</Alert> : null}
      <H1>{universal_quoter && universal_quoter.name ? universal_quoter.name : ''}</H1>
      <div
        style={{
          display: 'flex',
          gap: 16,
          marginBottom: 24,
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {TOP_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => {
              setSelectedProductTab(tab.key);
              setSelectedVerticalTab(
                tab.key === 'universal_general' ? 'quoter_overview' : 'product_defaults'
              );
            }}
            style={{
              fontWeight: selectedProductTab === tab.key ? 700 : 400,
              color: selectedProductTab === tab.key ? '#1976d2' : '#333',
              background: 'none',
              border: 'none',
              fontSize: 18,
              padding: '8px 24px',
              borderBottom:
                selectedProductTab === tab.key ? '2px solid #1976d2' : '2px solid transparent',
              cursor: 'pointer'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', minHeight: 400 }}>
        <div
          style={{
            width: 220,
            borderRight: '1px solid #e0e0e0',
            padding: '24px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 8
          }}
        >
          {getVerticalTabs().map(tab => {
            const disabled = false;

            let color = '#333';

            if (disabled) color = '#bbb';
            else if (selectedVerticalTab === tab.key) color = '#1976d2';
            return (
              <button
                disabled={disabled}
                key={tab.key}
                onClick={() => {
                  if (!disabled) setSelectedVerticalTab(tab.key);
                }}
                style={{
                  fontWeight: selectedVerticalTab === tab.key ? 600 : 400,
                  color,
                  background: 'none',
                  border: 'none',
                  fontSize: 16,
                  padding: '10px 18px',
                  textAlign: 'left',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.6 : 1,
                  borderLeft:
                    selectedVerticalTab === tab.key ? '3px solid #1976d2' : '3px solid transparent',
                  backgroundColor: selectedVerticalTab === tab.key ? '#f5faff' : 'transparent'
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <div style={{ flex: 1, padding: 32 }}>{renderVerticalTabContent()}</div>
      </div>
    </>
  );
};

Show.propTypes = {
  name: PropTypes.string
};

export default Show;
